import { ENDPOINTS } from 'config/endpoints';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { ResponsePaginated } from 'crono-fe-common/types/response';
import { UserSearch } from 'crono-fe-common/types/DTO/userSearch';
import { User } from 'crono-fe-common/types/user';

export default function useSearchUsers(
  searchParameters: UserSearch,
  enabled = true,
) {
  const request: Request = {
    url: ENDPOINTS.user.search,
    config: {
      method: 'post',
      data: { data: searchParameters },
    },
  };

  return useQuery<ResponsePaginated<User[]>, CronoError>({
    queryKey: [ENDPOINTS.user.search, searchParameters],
    queryFn: () => client(request),
    enabled,
  });
}

export function useSearchUsersInfiniteScroll(
  searchParameters: Omit<UserSearch, 'offset'>,
) {
  const requestConfig = {
    queryKey: [ENDPOINTS.user.search, searchParameters],
    queryFn: ({ pageParam = 0 }) => {
      const request: Request = {
        url: ENDPOINTS.user.search,
        config: {
          method: 'post',
          data: { data: { ...searchParameters, offset: pageParam } },
        },
      };
      return client(request);
    },
    getNextPageParam: (
      lastPage: ResponsePaginated<User[]>,
      pages: ResponsePaginated<User[]>[],
    ) => {
      if (!lastPage.data.data) {
        return undefined;
      }

      const howManyLoaded = pages.reduce(
        (sum, page) => sum + (page.data.data?.length ?? 0),
        0,
      );

      const checkIfHasNextPage =
        lastPage.data.data.length === 0 ||
        lastPage.data.data.length < searchParameters.limit;

      if (checkIfHasNextPage) {
        return undefined;
      }

      return howManyLoaded;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  };

  return useInfiniteQuery<ResponsePaginated<User[]>, CronoError>(requestConfig);
}
